import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Mail, Phone, Calendar, Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { contactService } from '@/api/services';
import { useTranslation } from '@/hooks/useTranslation';

const ContactForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false,
    // Honeypot-Feld (sollte leer bleiben)
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoadTime, setFormLoadTime] = useState<number>(Date.now());
  
  useEffect(() => {
    // Formular-Ladezeit festhalten
    setFormLoadTime(Date.now());
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, consent: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prüfen, ob das Honeypot-Feld ausgefüllt wurde
    if (formData.website) {
      // Wenn das Honeypot-Feld ausgefüllt wurde, täuschen wir einen erfolgreichen Absendeprozess vor,
      // ohne tatsächlich etwas zu senden (Bot-Abwehr)
      simulateSuccess();
      return;
    }
    
    // Zeitprüfung (Wenn das Formular in weniger als 3 Sekunden ausgefüllt wurde, ist es wahrscheinlich ein Bot)
    const timeSinceLoad = Date.now() - formLoadTime;
    if (timeSinceLoad < 3000) {
      // Vermutlich ein Bot - Erfolg vortäuschen ohne zu senden
      simulateSuccess();
      return;
    }
    
    // Formular wird wirklich abgesendet
    setIsSubmitting(true);
    
    try {
      // Honeypot-Feld entfernen, bevor die Daten gesendet werden
      const { website, ...submitData } = formData;
      
      // Verwendung des Contact-Service
      const response = await contactService.submitContactForm(submitData);
      
      if (response.success) {
        toast({
          title: t('contact.form.success'),
          description: t('contact.form.successDescription'),
          variant: "default",
        });
        
        // Formular zurücksetzen
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          consent: false,
          website: ''
        });
      } else {
        toast({
          title: t('contact.form.error'),
          description: response.message || t('contact.form.errorDescription'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t('contact.form.error'),
        description: t('contact.form.errorTechnical'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Simuliert einen erfolgreichen Absendeprozess ohne tatsächliche Datenübermittlung
  const simulateSuccess = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: t('contact.form.success'),
        description: t('contact.form.successDescription'),
        variant: "default",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        consent: false,
        website: ''
      });
    }, 1500);
  };
  
  return (
    <section id="contact" className="section bg-gray-50">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            {t('contact.cta.title')}
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {t('contact.cta.description')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="bg-blue-700 text-white p-8 lg:p-12">
              <h3 className="text-2xl font-bold mb-6">{t('contact.info.title')}</h3>
              <p className="mb-8">
                {t('contact.info.description')}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <Phone className="h-6 w-6 mr-4 mt-1" />
                  <div>
                    <h4 className="font-bold">{t('contact.info.callUs')}</h4>
                    <p className="text-blue-100">
                      <a href="tel:+4922659999775" className="hover:text-white transition-colors">
                        +49 2265 9999 775
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Mail className="h-6 w-6 mr-4 mt-1" />
                  <div>
                    <h4 className="font-bold">{t('contact.info.emailUs')}</h4>
                    <p className="text-blue-100">
                      <a href="mailto:info@splusd-solutions.com" className="hover:text-white transition-colors">
                        info@splusd-solutions.com
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Calendar className="h-6 w-6 mr-4 mt-1" />
                  <div>
                    <h4 className="font-bold">{t('contact.info.officeHours')}</h4>
                    <p className="text-blue-100">{t('contact.info.officeHoursTime')}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12">
                <h4 className="font-bold mb-2">{t('contact.info.expectationsTitle')}</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 mt-0.5 text-blue-300" />
                    <span>{t('contact.info.expectations.1')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 mt-0.5 text-blue-300" />
                    <span>{t('contact.info.expectations.2')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 mt-0.5 text-blue-300" />
                    <span>{t('contact.info.expectations.3')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 mt-0.5 text-blue-300" />
                    <span>{t('contact.info.expectations.4')}</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="p-8 lg:p-12">
              <h3 className="text-2xl font-bold mb-6">{t('contact.form.title')}</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot-Feld (für Bots) - ist mit CSS versteckt */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">Website (nicht ausfüllen)</label>
                  <Input
                    id="website"
                    name="website"
                    type="text"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.name')} *</label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.email')} *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.phone')}</label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.company')}</label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.message')}</label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="w-full"
                  />
                </div>
                
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="consent" 
                    checked={formData.consent} 
                    onCheckedChange={handleCheckboxChange}
                    required
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="consent"
                      className="text-sm font-medium leading-none text-gray-700"
                    >
                      {t('contact.form.consent')} *
                    </Label>
                    <p className="text-xs text-gray-500">
                      {t('contact.form.consentInfo')}{' '}
                      <a href="/datenschutz" className="text-blue-600 hover:underline">
                        Datenschutzerklärung
                      </a>.
                    </p>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    t('contact.form.submit')
                  )}
                </Button>
                
                <p className="text-xs text-gray-500 mt-4">
                  {t('contact.form.disclaimer')}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm; 